// React import
import React, { useEffect, useState, useCallback, useRef, Fragment } from "react";
//Material component import
import { ListItem } from "@mui/material";
import { CircularProgress } from "@mui/material";
// custom component import
import { useMsal } from "@azure/msal-react";
import styles from "./DailyProductionInsight.module.css";
import ProductionPlanComparsion from './ProductionPlanComparsion';
import ChangeoverTimeVisibility from './ChangeoverTimeVisibility';
import PriorityGraphInsight from './PriorityGraphInsight';
import AdoptionPlans from './AdoptionPlans';
import PlanChangeResonTrend from './PlanChangeResonTrend';
import ChangeSystemVsMMPlan from './ChangeSystemVsMMPlan';
import MultiSelectWithSearchInput from '../../components/dropdown/MultiSelectWithInput';
import { scheduleCleanup, getFilterDetails } from "utils/generateFilterObject";
import { transformFilters } from "utils/transformFilters";
import { getProductionPlanComparison, getSystemVsMMPlanData, 
    getAdoptionPlanData, getPlanChangeReasonTrend,
    getPriorityGraphInsight, getChangeOverTimeDetails } from "Samarth Module/api/DailyProductionInsightAPI";

// Helper function to determine MM Flag based on current day and time
const getMMFlag = () => {
    const now = new Date();
    const day = now.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const totalMinutes = hours * 60 + minutes;
    const threshold = 13 * 60; // 1:00 PM = 13:00
    
    let mmFlag = 'M1';
    // Monday to Thursday 1:00 PM -> M1
    // Thursday 1:01 PM to Sunday 11:59 PM -> M2
    if (day === 4) { // Thursday
        mmFlag = totalMinutes <= threshold ? 'M1' : 'M2';
    } else if (day === 5 || day === 6 || day === 0) { // Friday, Saturday, Sunday
        mmFlag = 'M2';
    }
    
    return mmFlag;
};

// Helper function to get current ISO week number
const getCurrentISOWeekNumber = () => {
    const now = new Date();
    const tmpDate = new Date(now.getTime());
    tmpDate.setHours(0, 0, 0, 0);
    tmpDate.setDate(tmpDate.getDate() + 3 - ((tmpDate.getDay() + 6) % 7));
    const week1 = new Date(tmpDate.getFullYear(), 0, 4);
    const currentWeek = 1 + Math.round(
        ((tmpDate.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7
    );
    
    return currentWeek;
};

const DailyProductionInsight = ()=>{
    const pageType = 'Daily Production Insights';
    const { accounts } = useMsal();
    const activeAccount = accounts[0] || null;
    const clearFiltersRef = useRef(false);
    const [filterObject, setFilterObject] = useState([]);
    const [changeFilterObject, setChangeFilterObject] = useState([]);
    const [filterObjectOption, setFilterObjectOption] = useState({});
    const [isPPComLoading, setIsPPComLoading] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const [firstApiCall, setFirstApiCall] = useState(true);
    const [ppComparisonData, setPpComparisonData] = useState([]);
    const [snackBarObject, setSnackBarObject] = useState({
        snackbarOpen: false,
        message: "",
        type: "error",
    });
    const [isSysVsMMLoading, setIsSysVsMMLoading] = useState(false);
    const [sysVsMMData, setSysVsMMData] = useState([]);
    const [isAdoptionLoading, setIsAdoptionLoading] = useState(false);
    const [adoptionPlanData, setAdoptionPlanData] = useState([]);
    const [isPlanChangeReasonLoading, setIsPlanChangeReasonLoading] = useState(false);
    const [planChangeReasonData, setPlanChangeReasonData] = useState([]);
    const [isPGILoading, setIsPGILoading] = useState(false);
    const [isChangeOverTimeLoading, setIsChangeOverTimeLoading] = useState(false);
    const [priorityGraphInsightData, setPriorityGraphInsightData] = useState([]);
    const [changeOverTimeDetails, setChangeOverTimeDetails] = useState([]);
    //api call for table data, column, filter data on page load
    // clean filter data
    useEffect(() => {
        scheduleCleanup();
    }, []);
    const getFilterOptionDetails = useCallback(async()=>{
        const resData =  await getFilterDetails(pageType, 'Detailed view');
        setFilterObjectOption(resData?.data?.filters || {});
    }, []);
    useEffect(() => {
        getFilterOptionDetails();
    }, [getFilterOptionDetails]);

    const handleMultiSelectChange = useCallback((label, values) => {
        //onchange API call
        let filterData = [...filterObject];
        let tempData = filterData?.map(filter => filter.label === label ? { ...filter, selectedData: values }: filter);
        
        // Check if there are any selected filters
        const hasSelection = tempData.some((filter) => {
            const selected = filter?.selectedData;
            if (Array.isArray(selected)) return selected.length > 0;
            if (typeof selected === 'string') return selected.trim().length > 0;
            return selected !== undefined && selected !== null;
        });
        
        clearFiltersRef.current = !hasSelection;
        setChangeFilterObject(tempData);

        setFilterObject(prev =>
            prev.map(filter => filter.label === label ? { ...filter, selectedData: values }: filter)
        );
    }, [filterObject]);    
    const generatePayload = useCallback(() => {
        const shouldClearFilters = clearFiltersRef.current;
        clearFiltersRef.current = false;
        const transformedFilters = transformFilters(changeFilterObject);
        return {
            email: activeAccount.username,
            type: pageType,
            role: activeAccount?.idTokenClaims?.roles,
            filterData: transformedFilters,
            clearFilters: shouldClearFilters
        };
    }, [activeAccount, changeFilterObject]);

    //api call for Production plan comparison, filter data on page load
    const fetchProductionPlanComparisonData = useCallback(async () => {
        setIsPPComLoading(true);
        try{
            const payload = generatePayload();
            const responseData = await getProductionPlanComparison(payload);
            setFilterObject(responseData?.filters || []);
            setPpComparisonData(responseData?.prod_plan_comparison || []);
        } catch (error) {
            setSnackBarObject({ snackbarOpen: true, message:error.message, type: "error" });
        }finally{
            setFirstApiCall(false);
            setIsPPComLoading(false);
        }
    }, [generatePayload]);

    useEffect(() => {
        if(!initialLoad){
            fetchProductionPlanComparisonData();
            fetchPriorityGraphInsight();
            fetchAdoptionPlanData();
            fetchPlanChangeReasonTrend();
            fetchSystemVsMMPlanData();
            fetchChangeOverTimeDetails();
        }
        setInitialLoad(false);
    }, [fetchProductionPlanComparisonData, initialLoad]);

    const fetchSystemVsMMPlanData = useCallback(async()=>{
        try{
            setIsSysVsMMLoading(true);
            const payload = generatePayload();
            const responseData = await getSystemVsMMPlanData(payload);
            setSysVsMMData(responseData?.system_mm_plan_data || [])
        }catch(error){
            setSnackBarObject({ snackbarOpen: true, message:error.message, type: "error" });
        }finally{
            setIsSysVsMMLoading(false)
        }
    }, [generatePayload]);

    const fetchAdoptionPlanData = useCallback(async()=>{
        setIsAdoptionLoading(true);
        try{
            const payload = generatePayload();
            const responseData = await getAdoptionPlanData(payload);
            setAdoptionPlanData(responseData?.adoption_insights_data || []);
        } catch (error){
            setSnackBarObject({ snackbarOpen: true, message:error.message, type: "error" });
        } finally{
            setIsAdoptionLoading(false);
        }
    }, [generatePayload]);

    const fetchPlanChangeReasonTrend = useCallback(async()=>{
        setIsPlanChangeReasonLoading(true);
        try{
            const payload = generatePayload();
            const responseData = await getPlanChangeReasonTrend(payload);
            setPlanChangeReasonData(responseData?.plan_change_reason_trend || []);
        } catch (error){
            setSnackBarObject({ snackbarOpen: true, message:error.message, type: "error" });
        } finally{
            setIsPlanChangeReasonLoading(false);
        }
    }, [generatePayload]);

    // api call for Priority graph insight
    const fetchPriorityGraphInsight = useCallback(async()=>{
        setIsPGILoading(true);
        try{
            const mmFlag = getMMFlag();
            const currentWeek = getCurrentISOWeekNumber();
            
            const payload = {...generatePayload(), 'MM Flag': mmFlag, 'System Flag':'S1', 'Week': currentWeek};
            const responseData = await getPriorityGraphInsight(payload);
            setPriorityGraphInsightData(responseData?.prioritygraphinsight_data || []);
        } catch (error){
            setSnackBarObject({ snackbarOpen: true, message:error.message, type: "error" });
        } finally{
            setIsPGILoading(false);
        }
    }, [generatePayload]);
    
    // api call for Changeover Time Visibility
    const fetchChangeOverTimeDetails = useCallback(async()=>{
        setIsChangeOverTimeLoading(true);
        try{
            const payload = {...generatePayload()};
            const responseData = await getChangeOverTimeDetails(payload);
            setChangeOverTimeDetails(responseData?.data || []);
        } catch (error){
            setSnackBarObject({ snackbarOpen: true, message:error.message, type: "error" });
        } finally{
            setIsChangeOverTimeLoading(false);
        }
    }, [generatePayload]);

    return (
        <Fragment>
            {/* Dailyphasing Insights */}
            <div className={styles.container}>
                { firstApiCall ? 
                <div className={styles.loaderContainer}><CircularProgress /></div> :<>
                <div className={styles.filterDiv}>
                    {filterObject?.length > 0 && filterObject?.filter(filter => filter.isFavourite)?.map(filter => (
                        <ListItem key={filter.label} className={styles.listCustomClass}>
                        <MultiSelectWithSearchInput
                            key={filter.label}
                            label={filter.label}
                            options={filterObjectOption[filter.label]}
                            filterValues={filter.selectedData}
                            handleFilter={handleMultiSelectChange}
                            disabled={false}
                            isLabelShow={true}
                        />
                        </ListItem>
                    ))}
                </div>
                <div className={styles.innerContainer}>
                    <div className={styles.graphContainer}>
                        <div className={styles.compInsigtText}><span>Comparative insight</span></div>
                        <div className={styles.scrollDiv}>
                            {ppComparisonData && <ProductionPlanComparsion data={ppComparisonData} loader={isPPComLoading}/>}
                            <ChangeoverTimeVisibility data={changeOverTimeDetails} loader={isChangeOverTimeLoading}/>
                            <PriorityGraphInsight data={priorityGraphInsightData} loader={isPGILoading}/>
                        </div>
                    </div>
                    <div className={styles.graphContainer}>
                        <div className={styles.compInsigtText}><span>Adoption insight</span></div>
                        <div className={styles.scrollDiv}>
                            <AdoptionPlans data={adoptionPlanData} loader={isAdoptionLoading} />
                            <PlanChangeResonTrend data={planChangeReasonData} loader={isPlanChangeReasonLoading}/>
                            <ChangeSystemVsMMPlan data={sysVsMMData} loader={isSysVsMMLoading}/>
                        </div>
                    </div>
                </div></>}
            </div>
        </Fragment>
    );
}
export default DailyProductionInsight;